import { useWatch } from 'react-hook-form';
import { UnitField } from 'v4/shared/components/forms/input/UnitField';
import { unitFieldUtils } from 'v4/shared/components/forms/input/unit-field-utils';
import { FormField } from 'v5/design-system/Form';
import type { MeasurementValue } from 'v5/core/entity-values/values.types';
import { useFieldContext } from '../FieldContext';
import { EmptyField } from './EmptyField';
import { FieldControlBase } from './FieldControlBase';
import type { FieldSpecOf } from 'v5/core/entity-schema/entitySchema.types';
import type { FieldFrameSpec, FieldPartProps } from './fieldComponent.types';

function hasAmount(value: MeasurementValue): boolean {
  return value.unit === 'ft'
    ? value.feet.trim() !== '' || value.inches.trim() !== ''
    : value.amount.trim() !== '';
}

/** Amount + unit select (`UnitValue`). */
export function MeasurementField({
  spec,
  path,
  hideLabel
}: FieldPartProps<
  FieldFrameSpec & Pick<FieldSpecOf<'measurement'>, 'unit' | 'unitLocked'>
>) {
  const ctx = useFieldContext();
  const readOnly = ctx.mode === 'display';
  const measurement: MeasurementValue | undefined = useWatch({ name: path });

  if (readOnly && (measurement === undefined || !hasAmount(measurement))) {
    return <EmptyField spec={spec} path={path} hideLabel={hideLabel} />;
  }

  return (
    <FormField
      name={path}
      render={({ field }) => {
        const value: MeasurementValue =
          field.value ??
          unitFieldUtils.getUnitValueFromImperial(null, spec.unit);
        return (
          <FieldControlBase spec={spec} hideLabel={hideLabel}>
            <UnitField
              variant={
                ctx.variant === 'plain' && ctx.mode === 'edit'
                  ? 'inline'
                  : ctx.variant
              }
              size={ctx.size}
              disableUnitSelect={spec.unitLocked}
              mode="edit"
              readOnly={readOnly}
              value={value}
              onChange={field.onChange}
              onBlurWithin={field.onBlur}
            />
          </FieldControlBase>
        );
      }}
    />
  );
}
